/**
 * DraftCard — one `awaiting_approval` draft in the bulk-send wizard: read it,
 * optionally edit it inline, then approve. An edit
 * always carries BOTH `subject` and `body` (never just the changed field) so
 * `edited_before_approval` is set correctly whether or not the subject line
 * itself changed (ruling 46's approve body `{subject?, body?}`).
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { OutreachDraft } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { PencilSimple } from '../../../design-system/icons';
import { MergeTagChips } from './MergeTagChips';
import { RichBody } from './richBody';
import { ComposerEditor, type ComposerEditorHandle } from './composerEditor/ComposerEditor';
import { CharCounter } from './CharCounter';
import { BODY_LIMIT, BODY_OVER_COPY, SUBJECT_LIMIT, SUBJECT_OVER_COPY, bodyOverCap, subjectOverCap } from './draftCaps';
import { draftDueLabel, draftTextErrors, incompleteDraftCopy, type DraftSaveError } from './draftLabels';

type EditField = 'subject' | 'body';

export interface DraftCardEdit {
  subject?: string;
  body?: string;
  /** The editor's formatting for `body`; the server sanitises it and derives the text from it. */
  bodyHtml?: string;
}

const FIRST_TOUCH_LINK_NOTE = "The first email can't contain a link. Add links in a follow-up.";

export interface DraftCardProps {
  draft: OutreachDraft;
  canWrite: boolean;
  busy?: boolean;
  error?: string | null;
  /** A server rejection tied to one field (DRAFT_SUBJECT_REQUIRED / DRAFT_BODY_REQUIRED). */
  fieldError?: DraftSaveError | null;
  /** Drops a server error the user has acted on: editing the flagged field, or Cancel. */
  onDismissError?: () => void;
  /** 'editOnly' hides the plain Approve: the wizard approves in bulk with its schedule. A first touch is always edit-only. */
  approveMode?: 'always' | 'editOnly';
  /** Shown under the editor — e.g. that saving approves this draft on its own. */
  editNote?: string;
  onApprove: (edit?: DraftCardEdit) => void;
  /** Lets a bulk approver hold off while this card has unsaved edits. */
  onEditingChange?: (editing: boolean) => void;
  /** 'wizard' (P32 Q10): name and the mail only — no kind or due line — and a pencil icon starts the inline edit. */
  variant?: 'default' | 'wizard';
  /** Wizard only: the "Draft N of M" menu, shown at the card's top right. */
  draftMenu?: ReactNode;
  /** Wizard only: "Write a new draft" in the footer. The earlier drafts stay in the menu. */
  onWriteNew?: () => void;
}

function SourceBadge({ source, edited }: { source: OutreachDraft['draftSource']; edited: boolean }) {
  if (source === 'agent') return <span className="rounded-full bg-(--brand-100) px-2 py-0.5 text-[11px] font-medium text-(--brand-600)">{edited ? 'Edited by you' : 'Written by the assistant'}</span>;
  if (source === 'template') return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">From template</span>;
  return null;
}

export function DraftCard({ draft, canWrite, busy, error, fieldError, onDismissError, approveMode = 'always', editNote, onApprove, onEditingChange, variant = 'default', draftMenu, onWriteNew }: DraftCardProps) {
  const { message, entry } = draft;
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(message.subject ?? '');
  const [body, setBody] = useState(message.bodyText);
  const [bodyHtml, setBodyHtml] = useState(message.bodyRichHtml ?? '');
  // The rich editor reads its seed once per mount: a new key re-seeds it after a cancel or a resync.
  const [editorKey, setEditorKey] = useState(0);
  const editorHandle = useRef<ComposerEditorHandle>(null);
  const name = entry.displayName ?? entry.username;
  const isWizard = variant === 'wizard';
  const isFirstTouch = message.kind === 'initial';
  const editOnly = approveMode === 'editOnly' || isFirstTouch;
  const linkNoteId = useId();

  // Cards are keyed by entryId, not message.id (DraftsStep), so this
  // component instance can persist across a draft regeneration that swaps in a
  // new message, or simply keep showing a message whose content changes
  // server-side (another admin editing the same draft, or any in-place
  // correction) while this card is never opened for editing. Resync from the
  // new content rather than keep showing stale text. The React-recommended
  // "adjust state when a prop changes" shape (plain state compared during
  // render, not an effect) — setting state conditionally here re-renders
  // before paint, so there's no stale-then-corrected flash.
  //
  // The comparison is against this *synced* snapshot — the last props we
  // actually resynced from — not against the current local subject/body.
  // Comparing against local state would misfire right after our own
  // successful save: local already holds the just-saved text, but the
  // props haven't caught up to it yet (the refetch is still in flight), and
  // that lag would otherwise look identical to "someone else changed it"
  // and snap the display straight back to the stale pre-edit text — the
  // exact flash Finding 1 fixed. Comparing against the synced snapshot (still
  // the pre-edit value, since we haven't resynced from props since the edit
  // started) means that lag resolves silently once the real refetch lands:
  // the incoming text already matches what's on screen.
  const [synced, setSynced] = useState({ id: message.id, subject: message.subject ?? '', body: message.bodyText, bodyHtml: message.bodyRichHtml ?? '' });
  const idChanged = message.id !== synced.id;
  const contentChanged =
    !idChanged &&
    !editing &&
    ((message.subject ?? '') !== synced.subject || message.bodyText !== synced.body || (message.bodyRichHtml ?? '') !== synced.bodyHtml);
  if (idChanged || contentChanged) {
    setSynced({ id: message.id, subject: message.subject ?? '', body: message.bodyText, bodyHtml: message.bodyRichHtml ?? '' });
    setSubject(message.subject ?? '');
    setBody(message.bodyText);
    setBodyHtml(message.bodyRichHtml ?? '');
    setEditorKey((k) => k + 1);
    // An id swap means the message being edited no longer exists server-side
    // (Finding 4): discard the stale edit outright, regardless of content.
    if (idChanged && editing) {
      setEditing(false);
      onEditingChange?.(false);
    }
  }

  // A save is optimistic about outcome, not about time: the editor stays open —
  // showing exactly what was submitted, never stale props — until the caller's
  // busy flag (savingIds/approvingIds) clears. Only a clean settle (no error)
  // returns the card to read-only; a failed save leaves the edit open so the
  // text and the error are visible together and a retry is one click away.
  const prevBusyRef = useRef(busy);
  useEffect(() => {
    const wasBusy = prevBusyRef.current;
    prevBusyRef.current = busy;
    if (wasBusy && !busy && editing && !error && !fieldError) {
      setEditing(false);
      onEditingChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, error, fieldError]);

  // The server keeps the stored subject when a blank one is sent, so clearing it would show one subject here and send another.
  const subjectCleared = (message.subject ?? '').trim() !== '' && subject.trim() === '';
  const textErrors = draftTextErrors(draft.kind, subject, body);
  const subjectOver = isFirstTouch && subjectOverCap(subject);
  const subjectError = textErrors.subject ?? (subjectCleared ? 'Add a subject before saving.' : undefined) ?? (subjectOver ? SUBJECT_OVER_COPY : undefined);
  const overCap = isFirstTouch && bodyOverCap(body);
  const bodyError = textErrors.body ?? (overCap ? BODY_OVER_COPY : undefined);
  const incomplete = !!subjectError || !!bodyError;
  const shownSubjectError = subjectError ?? (fieldError?.field === 'subject' ? fieldError.message : undefined);
  const shownBodyError = bodyError ?? (fieldError?.field === 'body' ? fieldError.message : undefined);
  const subjectErrorId = `draft-subject-error-${message.id}`;
  const bodyErrorId = `draft-body-error-${message.id}`;

  const startEdit = () => {
    setEditing(true);
    onEditingChange?.(true);
  };

  // Wizard variant: the pencil unmounts while editing, so keyboard focus goes to Subject
  // on open and back to the pencil when the editor closes (save or cancel).
  const pencilRef = useRef<HTMLButtonElement>(null);
  const wasEditingRef = useRef(editing);
  useEffect(() => {
    if (isWizard && wasEditingRef.current && !editing) pencilRef.current?.focus();
    wasEditingRef.current = editing;
  }, [editing, isWizard]);

  const subjectRef = useRef<HTMLInputElement>(null);
  const editorHostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!editing || !fieldError?.field) return;
    if (fieldError.field === 'subject') subjectRef.current?.focus();
    else editorHostRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus();
    // Keyed on the error object alone: each failure is a fresh object, so a repeat rejection refocuses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldError]);

  const editField = (field: EditField, value: string) => {
    if (field === 'subject') setSubject(value);
    else setBody(value);
    if (fieldError?.field === field) onDismissError?.();
  };

  const cancelEdit = () => {
    if (error || fieldError) onDismissError?.();
    setEditing(false);
    onEditingChange?.(false);
    setSubject(message.subject ?? '');
    setBody(message.bodyText);
    setBodyHtml(message.bodyRichHtml ?? '');
    setEditorKey((k) => k + 1);
  };

  // Mirrors TemplateEditor/TemplateStepEditor's caret tracking (and
  // MergeTagChips' own contract): a chip splices into whichever field the
  // caret was last in, not always the body — otherwise a tag clicked while
  // editing the subject silently lands in the wrong field. Handlers are
  // inlined on each field (not built via a props-factory) so the ref write
  // stays a direct event-handler call, not one routed through a function
  // value constructed during render.
  const caretRef = useRef<{ field: EditField; start: number; end: number } | null>(null);
  const rememberCaret = (field: EditField, el: HTMLInputElement) => {
    const fallback = el.value.length;
    caretRef.current = { field, start: el.selectionStart ?? fallback, end: el.selectionEnd ?? fallback };
  };
  const insertMergeTag = (tag: string) => {
    const at = caretRef.current ?? { field: 'body' as EditField, start: 0, end: 0 };
    if (at.field === 'body') {
      editorHandle.current?.insertTextAtCursor(tag);
      return;
    }
    setSubject(`${subject.slice(0, at.start)}${tag}${subject.slice(at.end)}`);
    const caret = at.start + tag.length;
    caretRef.current = { ...at, start: caret, end: caret };
  };

  return (
    <article className="space-y-2 rounded-md border border-border p-3" aria-label={`Draft for ${name}`}>
      <header className="flex items-center gap-2">
        <div className="min-w-0">
          <div className="truncate text-body-sm-medium text-foreground">
            {name} <span className="text-muted-foreground">@{entry.username}</span>
          </div>
          {isWizard ? null : (
            <div className="text-caption-regular text-muted-foreground">
              {`${draft.kind === 'initial' ? 'First touch' : `Follow-up · Step ${message.step ?? '?'}`} · ${draftDueLabel(message.scheduledFor, new Date())}`}
            </div>
          )}
        </div>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <SourceBadge source={draft.draftSource} edited={message.editedBeforeApproval === true} />
          {isWizard ? draftMenu : null}
          {isWizard && canWrite && !editing ? (
            <Button ref={pencilRef} type="button" size="icon" variant="ghost" className="size-8" aria-label={`Edit email to ${name}`} title="Edit" disabled={busy} onClick={startEdit}>
              <PencilSimple size={16} aria-hidden="true" />
            </Button>
          ) : null}
        </span>
      </header>

      {editing ? (
        <div className="space-y-2">
          <Input
            aria-label="Subject"
            aria-invalid={shownSubjectError ? true : undefined}
            aria-describedby={shownSubjectError ? subjectErrorId : undefined}
            autoFocus={isWizard}
            value={subject}
            ref={subjectRef}
            onChange={(e) => editField('subject', e.target.value)}
            onFocus={(e) => rememberCaret('subject', e.currentTarget)}
            onClick={(e) => rememberCaret('subject', e.currentTarget)}
            onKeyUp={(e) => rememberCaret('subject', e.currentTarget)}
            onSelect={(e) => rememberCaret('subject', e.currentTarget)}
          />
          {isFirstTouch ? <CharCounter label="Subject" text={subject.trim()} limit={SUBJECT_LIMIT} /> : null}
          {shownSubjectError ? (
            <p id={subjectErrorId} role="alert" className="text-caption-regular text-destructive">
              {shownSubjectError}
            </p>
          ) : null}
          <div
            ref={editorHostRef}
            className="overflow-hidden rounded-md border border-input"
            onFocusCapture={() => {
              caretRef.current = { field: 'body', start: 0, end: 0 };
            }}
          >
            <ComposerEditor
              key={editorKey}
              handleRef={editorHandle}
              ariaLabel="Body"
              ariaInvalid={!!shownBodyError}
              autoFocus={false}
              liveRegion={false}
              allowLink={!isFirstTouch}
              ariaDescribedBy={[isFirstTouch ? linkNoteId : '', shownBodyError ? bodyErrorId : ''].filter(Boolean).join(' ') || undefined}
              initialText={body}
              initialHtml={bodyHtml}
              placeholder="Write the email…"
              onChange={(text, html) => {
                // A formatting-only change leaves the words as they were, so a server body error still stands.
                if (text !== body) editField('body', text);
                setBodyHtml(html);
              }}
              onSubmitShortcut={() => undefined}
              renderFooter={(toolbar) => <div className="border-t border-border bg-[#fafafa] px-3 py-2">{toolbar}</div>}
            />
          </div>
          {isFirstTouch ? <CharCounter label="Body" text={body} limit={BODY_LIMIT} /> : null}
          {shownBodyError ? (
            <p id={bodyErrorId} role="alert" className="text-caption-regular text-destructive">
              {shownBodyError}
            </p>
          ) : null}
          {isFirstTouch ? (
            <p id={linkNoteId} className="text-caption-regular text-muted-foreground">
              {FIRST_TOUCH_LINK_NOTE}
            </p>
          ) : null}
          <MergeTagChips onInsert={insertMergeTag} />
          {editNote ? <p className="text-caption-regular text-muted-foreground">{editNote}</p> : null}
        </div>
      ) : (
        // Renders the local (committed) subject/body, not message.* directly: once a save
        // settles, this is the just-submitted text, not whatever the slower draft-list
        // refetch has caught up to yet (Finding 1 — no revert-then-reappear flash).
        <div className="space-y-1">
          <div className="text-body-sm-medium text-foreground">{subject}</div>
          {bodyHtml ? (
            <RichBody html={bodyHtml} className="text-body-sm-regular text-muted-foreground" />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-body-sm-regular text-muted-foreground">{body}</pre>
          )}
          {incomplete && canWrite ? (
            <p role="alert" className="text-caption-regular text-destructive">
              {incompleteDraftCopy(draft.kind, subject, body) ?? (subjectOver ? SUBJECT_OVER_COPY : overCap ? BODY_OVER_COPY : null)}
            </p>
          ) : null}
        </div>
      )}

      {error && (
        <p role="alert" className="text-caption-regular text-destructive">
          {error}
        </p>
      )}

      <footer className="flex gap-2">
        {isWizard && onWriteNew && !editing ? (
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onWriteNew}>
            Write a new draft
          </Button>
        ) : null}
        {canWrite && !isWizard && !editing && !busy && (
          <Button type="button" size="sm" variant="outline" onClick={startEdit}>
            Edit
          </Button>
        )}
        {canWrite && editing && (
          <Button type="button" size="sm" variant="outline" onClick={cancelEdit} disabled={busy}>
            Cancel
          </Button>
        )}
        {canWrite && (!editOnly || editing) && (
          <Button
            type="button"
            size="sm"
            className="ml-auto"
            disabled={busy || incomplete}
            onClick={() => {
              const edit = editing ? { subject, body, ...(bodyHtml ? { bodyHtml } : {}) } : undefined;
              onApprove(edit);
              // Not optimistic: editing stays open until the busy-tracking effect
              // above sees the save settle (success closes it; a failure leaves it
              // open next to the error, see Finding 1/2).
            }}
          >
            {/* editOnly's onApprove only PATCHes (the caller approves separately,
                later) — "Save & approve" would contradict the editNote shown
                right above this button in that mode. */}
            {editing ? (editOnly ? 'Save' : 'Save & approve') : 'Approve'}
          </Button>
        )}
      </footer>
    </article>
  );
}
